import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { CartContext } from '../context/CartContext';
import { AuthContext } from '../context/AuthContext';
import { ordersAPI } from '../api/orders';
import { paymentAPI } from '../api/payment';
import toast from 'react-hot-toast';
import Loader from '../components/common/Loader';

const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:8000';
  const normalizedPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
  return `${baseUrl}${normalizedPath}`;
};

const Checkout = () => {
  const { cartItems, cartTotal, clearCart } = useContext(CartContext);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    phone_number: user?.phone_number || '',
    address: '',
    city: '',
    postal_code: '',
    notes: '',
  });
  const [imageErrors, setImageErrors] = useState({});

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setLoading(true);

    try {
      const orderData = {
        items: cartItems.map(item => ({
          product_id: item.id,
          quantity: item.quantity,
          size: item.size,
          color: item.color,
        })),
        shipping_address: `${formData.address}, ${formData.city}, ${formData.postal_code}`,
        phone_number: formData.phone_number,
        notes: formData.notes,
      };

      const order = await ordersAPI.create(orderData);
      const payment = await paymentAPI.initiate(order.id);
      
      clearCart();
      window.location.href = payment.payment_url;
      
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Checkout failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const shipping = cartTotal > 100 ? 0 : 10;
  const tax = cartTotal * 0.08;
  const total = cartTotal + shipping + tax;

  if (loading) return <Loader />;

  if (cartItems.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 text-center">
        <p className="text-gray-400 text-sm mb-4">Your cart is empty</p>
        <button
          onClick={() => navigate('/products')}
          className="text-sm text-gray-900 border-b border-gray-300 hover:border-gray-900 pb-0.5"
        >
          Continue shopping
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 lg:py-16">
      <div className="flex items-center mb-8 lg:mb-12">
        <button
          onClick={() => navigate('/cart')}
          className="text-xs text-gray-400 hover:text-gray-900 transition mr-4"
        >
          ← Back
        </button>
        <h1 className="text-xl lg:text-2xl font-light tracking-tight text-gray-900">
          Checkout
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        {/* Shipping Form */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              <div>
                <h2 className="text-xs font-light tracking-wide uppercase text-gray-400 mb-4">
                  Shipping Information
                </h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-400 uppercase tracking-wider mb-2">
                        Full Name
                      </label>
                      <input
                        type="text"
                        name="full_name"
                        value={formData.full_name}
                        onChange={handleChange}
                        required
                        className="w-full px-0 py-2 text-sm text-gray-900 border-b border-gray-200 focus:outline-none focus:border-gray-900 transition bg-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs text-gray-400 uppercase tracking-wider mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-0 py-2 text-sm text-gray-900 border-b border-gray-200 focus:outline-none focus:border-gray-900 transition bg-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs text-gray-400 uppercase tracking-wider mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone_number"
                        value={formData.phone_number}
                        onChange={handleChange}
                        required
                        className="w-full px-0 py-2 text-sm text-gray-900 border-b border-gray-200 focus:outline-none focus:border-gray-900 transition bg-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs text-gray-400 uppercase tracking-wider mb-2">
                        Address
                      </label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        required
                        className="w-full px-0 py-2 text-sm text-gray-900 border-b border-gray-200 focus:outline-none focus:border-gray-900 transition bg-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs text-gray-400 uppercase tracking-wider mb-2">
                        City
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        className="w-full px-0 py-2 text-sm text-gray-900 border-b border-gray-200 focus:outline-none focus:border-gray-900 transition bg-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs text-gray-400 uppercase tracking-wider mb-2">
                        Postal Code
                      </label>
                      <input
                        type="text"
                        name="postal_code"
                        value={formData.postal_code}
                        onChange={handleChange}
                        className="w-full px-0 py-2 text-sm text-gray-900 border-b border-gray-200 focus:outline-none focus:border-gray-900 transition bg-transparent"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs text-gray-400 uppercase tracking-wider mb-2">
                      Order Notes (Optional)
                    </label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      rows="3"
                      className="w-full px-0 py-2 text-sm text-gray-900 border-b border-gray-200 focus:outline-none focus:border-gray-900 transition bg-transparent resize-none"
                      placeholder="Special instructions..."
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <div className="bg-gray-50 p-6">
            <h2 className="text-xs font-light tracking-wide uppercase text-gray-400 mb-5">
              Order Summary
            </h2>
            
            {/* Cart Items */}
            <div className="space-y-4 mb-5 max-h-80 overflow-y-auto">
              {cartItems.map((item, idx) => {
                const imageUrl = getImageUrl(item.image);
                const hasError = imageErrors[`${item.id}-${item.size}-${item.color}`];
                const itemPrice = item.discount_price || item.price;
                
                return (
                  <div key={idx} className="flex gap-3">
                    <div className="w-12 h-14 bg-gray-100 flex-shrink-0 overflow-hidden">
                      {!hasError && imageUrl ? (
                        <img 
                          src={imageUrl} 
                          alt={item.title}
                          className="w-full h-full object-cover"
                          onError={() => setImageErrors(prev => ({ ...prev, [`${item.id}-${item.size}-${item.color}`]: true }))}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-[8px] text-gray-400">No img</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-900 truncate">{item.title}</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">
                        {item.size} / {item.color}
                      </p>
                      <p className="text-[10px] text-gray-400">Qty: {item.quantity}</p>
                    </div>
                    <p className="text-xs text-gray-900 whitespace-nowrap">
                      ${(itemPrice * item.quantity).toFixed(2)}
                    </p>
                  </div>
                );
              })}
            </div>
            
            {/* Price Summary */}
            <div className="border-t border-gray-200 pt-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Subtotal</span>
                <span className="text-gray-900">${cartTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Shipping</span>
                <span className="text-gray-900">
                  {shipping === 0 ? 'Free' : `$${shipping.toFixed(2)}`}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Tax (8%)</span>
                <span className="text-gray-900">${tax.toFixed(2)}</span>
              </div>
              <div className="border-t border-gray-200 pt-3 mt-3">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-900">Total</span>
                  <span className="text-base font-medium text-gray-900">${total.toFixed(2)}</span>
                </div>
              </div>
            </div>
            
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full mt-6 py-3 text-sm font-medium tracking-wide text-white bg-gray-900 hover:bg-gray-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Processing...' : 'Place Order & Pay'}
            </button>
            
            <div className="mt-4 text-center">
              <p className="text-[10px] text-gray-400">
                Free shipping on orders over $100
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;